/**
 * The phone's own record of a coach-tier purchase the store completed but our
 * server was never told about, and the rules for what to do with it later. A tier
 * CHANGE writes the same record (`CoachTierService.changeTier*Async`, #5611), so
 * "purchase" below covers both.
 *
 * The coach has PAID at the moment this is written, and until our server answers
 * nothing else anywhere knows it. What used to happen to that coach, what the two
 * halves of the recovery are, and why one device cannot cover both cases:
 * `design-docs/iap-subscriptions.md`, "What now recovers the throw route".
 *
 * Two things to know before changing anything here. The record is deliberately
 * NOT in `DEVICE_LEVEL_LOCALSTORAGE_KEYS` (`local-storage-hygiene.ts`), so
 * signing out wipes it: that list is for device-level state, and a store purchase
 * token belongs to one account. And this module imports neither Angular nor
 * Capacitor, so `coach-tier-pending-purchase.test.ts` runs it under mocha with a
 * plain `localStorage` shim — the same arrangement as `store-purchase-flow.ts`
 * and `local-storage-hygiene.ts` next door, and what makes every decision about
 * somebody's money testable without a store, a server or a browser.
 */
import { CoachTierValidateParams } from '@cf-shared/coach-billing-interface';
import { isCoachTierSettledRefusalCode } from '@cf-shared/store-validate-errors';
import { DateTime } from 'luxon';

/** Where the record is kept. User-scoped, so `clearUserScopedLocalStorage` wipes
 *  it on sign-out — see the header for why that is wanted. */
export const COACH_TIER_PENDING_PURCHASE_KEY = 'coachTierPendingPurchase';

/**
 * How long the phone keeps re-sending an unconfirmed purchase before giving up.
 *
 * There has to be a limit, because several answers keep the record rather than
 * settling it, and each of them can repeat for ever. Fourteen days matches the
 * server's own `PENDING_STORE_CLAIM_MAX_AGE_DAYS`, so the two halves of the same
 * recovery give up together rather than one outliving the other.
 *
 * **This limit, and nothing else, is what ends a purchase the store has already
 * reversed.** On Google an unacknowledged purchase is reversed after three days,
 * and the store then answers that the subscription is not active — but that
 * answer arrives as a free-text sentence rather than one of the codes
 * `isCoachTierSettledRefusalCode` knows, so it does NOT settle the record. The
 * phone goes on re-sending that dead purchase once per app open until the day
 * count above ends it. That costs one call per app open and is the safe
 * direction of the two, but it is why the age limit is load-bearing rather than
 * a tidy-up. Settling it properly needs a machine-readable code from the server,
 * which is a change on the other side of the wire.
 */
export const COACH_TIER_PENDING_PURCHASE_MAX_AGE_DAYS = 14;

/**
 * A coach-tier purchase the store completed and our server has not confirmed.
 *
 * It holds the WHOLE request rather than a token, so the retry re-sends exactly
 * what the purchase sent — including `replacedPurchaseToken`, without which a
 * coach whose purchase replaced their personal membership would recover into
 * paying for both.
 */
export interface CoachTierPendingPurchase {
  /** who bought it. The retry refuses to send another account's purchase, so a
   *  shared device can never attach one person's plan to another. */
  uid: string;
  /** when the store took the money, ISO-8601 in UTC. */
  rememberedAt_utc: string;
  /** the confirmation request that never got an answer. */
  params: CoachTierValidateParams;
}

/** Whether `rememberCoachTierPurchase` managed to write the record, and what
 *  storage threw when it did not (`error` is set only then). */
export interface RememberCoachTierPurchaseResult {
  stored: boolean;
  error?: unknown;
}

/** What the app should do with the record it found at start-up. */
export enum PendingCoachTierPurchaseAction {
  /** there is nothing written down. */
  Nothing = 'Nothing',
  /** re-send it and act on the answer. */
  Retry = 'Retry',
  /** too old to keep asking about — forget it, and report that we never
   *  recovered a purchase somebody paid for. */
  DropExpired = 'DropExpired',
  /** it belongs to somebody else who used this device. Leave it alone: it is
   *  not ours to send, and not ours to delete either. */
  SkipOtherAccount = 'SkipOtherAccount',
}

/**
 * Write the purchase down. Best-effort and never throws.
 *
 * Failing to remember must never break the purchase flow it is called from:
 * the coach's outcome is the same either way, and the only thing lost is the
 * automatic recovery. `localStorage` throws in a browser with storage disabled
 * and when the quota is full, so this is not theoretical.
 *
 * A new record replaces any earlier one. A coach retrying their own purchase
 * supersedes their previous attempt, and keeping both would only mean asking
 * about a transaction they have already moved on from — the same rule the
 * server's parked claim follows.
 *
 * @returns `stored: false`, with what storage threw, when the write was
 *          refused. The caller must report that, because only the caller has a
 *          Sentry client, and a coach who lost this record has lost their only
 *          automatic route back.
 */
export function rememberCoachTierPurchase(record: CoachTierPendingPurchase): RememberCoachTierPurchaseResult {
  try {
    localStorage.setItem(COACH_TIER_PENDING_PURCHASE_KEY, JSON.stringify(record));
    return { stored: true };
  } catch (error) {
    // not reported HERE: the answer carries the error to the caller, who has a
    // Sentry client.
    return { stored: false, error };
  }
}

/**
 * Read the record back, or undefined when there is none.
 *
 * Anything unreadable — absent, not JSON, or missing the fields a retry needs —
 * reads as "no record". A half-written value cannot be re-sent to the server, so
 * treating it as absent is the only useful answer; `decidePendingCoachTierPurchase`
 * then says to do nothing, and the next purchase overwrites it.
 */
export function readCoachTierPendingPurchase(): CoachTierPendingPurchase | undefined {
  let raw: string | null;
  try {
    raw = localStorage.getItem(COACH_TIER_PENDING_PURCHASE_KEY);
  } catch {
    // storage refused the read: there is no record we can act on, which is the
    // same answer as there being none.
    return undefined;
  }
  if (!raw) return undefined;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // a half-written value cannot be re-sent to the server, so it is no record.
    return undefined;
  }
  return isUsableRecord(parsed) ? parsed : undefined;
}

/**
 * Forget the record, as long as it belongs to the account asking.
 *
 * **The owner check is the point of the parameter, not a formality.** A record
 * left behind by somebody else who used this device — an app killed before the
 * sign-out wipe finished, a crash — is that person's only automatic route back
 * to a plan they paid for. Deleting it because THIS account finished a purchase
 * of its own would destroy it silently. `decidePendingCoachTierPurchase` refuses
 * to SEND another account's record for the same reason; this is the other half.
 *
 * Best-effort and never throws, like `rememberCoachTierPurchase`.
 *
 * @param ownerUid the signed-in account. A record belonging to anybody else is
 *                 left exactly where it is.
 */
export function forgetCoachTierPendingPurchase(ownerUid: string | undefined): void {
  const record = readCoachTierPendingPurchase();
  if (record && record.uid !== ownerUid) return;

  try {
    localStorage.removeItem(COACH_TIER_PENDING_PURCHASE_KEY);
  } catch {
    // nothing to do about it, and a record we failed to delete is only re-sent
    // once more before its age limit drops it.
  }
}

/**
 * What to do with the record, given who is signed in and what time it is.
 *
 * Kept apart from everything that does the work so the whole decision can be
 * tested without a store, a server or a clock.
 */
export function decidePendingCoachTierPurchase(
  record: CoachTierPendingPurchase | undefined,
  signedInUid: string | undefined,
  now: DateTime,
): PendingCoachTierPurchaseAction {
  if (!record) return PendingCoachTierPurchaseAction.Nothing;

  // Whose it is comes FIRST, before the age check, and that order is the point:
  // deleting a record belonging to somebody else who used this device would
  // destroy their recovery, and we cannot even tell whether it is expired for
  // them without knowing it is theirs.
  if (!signedInUid || record.uid !== signedInUid) {
    return PendingCoachTierPurchaseAction.SkipOtherAccount;
  }

  const rememberedAt = DateTime.fromISO(record.rememberedAt_utc);
  const tooOld = !rememberedAt.isValid ||
    now.diff(rememberedAt, 'days').days > COACH_TIER_PENDING_PURCHASE_MAX_AGE_DAYS;
  if (tooOld) {
    // an unreadable timestamp counts as too old on purpose: we cannot tell how
    // long it has been waiting, and a record that can never expire would be
    // re-sent at every app open for ever.
    return PendingCoachTierPurchaseAction.DropExpired;
  }

  return PendingCoachTierPurchaseAction.Retry;
}

/**
 * Has our server settled this purchase, so the record can be thrown away?
 *
 * The split this makes is between "somebody has answered the question" and "we
 * failed to write the answer down", and it is the same split the server's own
 * parked claim makes for the same reason: a passing database failure must not
 * throw away a purchase somebody paid for.
 *
 * So it is YES for a success and for a refusal that settles the matter, and NO
 * for everything else — including our server answering that it could not reach
 * the store, that it could not find the user record, or that it failed to write
 * the tier down. A kept record costs one call at the next app open and stops for
 * good at the age limit above.
 *
 * @param errorCode the `error` field of the confirmation answer; absent means it
 *                  succeeded.
 */
export function isCoachTierPurchaseSettled(errorCode: string | undefined): boolean {
  if (!errorCode) return true;
  return isCoachTierSettledRefusalCode(errorCode);
}

/**
 * Our server answered the confirmation: forget the record if that answer settled
 * the purchase, and keep it for the next app open if it did not.
 *
 * The one rule both writers of the record apply when their confirmation call
 * resolves — the first purchase in `IAPService.purchaseCoachTierAsync` and the
 * tier change in `CoachTierService` — so they cannot drift apart.
 *
 * @param ownerUid  the signed-in account; see `forgetCoachTierPendingPurchase`.
 * @param errorCode the `error` field of the confirmation answer.
 */
export function forgetCoachTierPendingPurchaseIfSettled(
  ownerUid: string | undefined,
  errorCode: string | undefined,
): void {
  if (isCoachTierPurchaseSettled(errorCode)) {
    forgetCoachTierPendingPurchase(ownerUid);
  }
}

/** Does this look like a record a retry could actually send? Guards against a
 *  value written by an older version of the app, or a half-written one. */
function isUsableRecord(value: unknown): value is CoachTierPendingPurchase {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<CoachTierPendingPurchase>;
  if (typeof candidate.uid !== 'string' || candidate.uid === '') return false;
  if (typeof candidate.rememberedAt_utc !== 'string') return false;

  const params = candidate.params as CoachTierValidateParams | undefined;
  if (!params) return false;
  if (params.provider === 'apple') {
    return typeof params.transactionId === 'string' && params.transactionId !== '' &&
      typeof params.originalTransactionId === 'string' && params.originalTransactionId !== '';
  }
  if (params.provider === 'googlePlay') {
    // Google's lookup needs the product as well as the token; a record missing
    // either can never be answered, so it is not usable.
    return typeof params.purchaseToken === 'string' && params.purchaseToken !== '' &&
      typeof params.productId === 'string' && params.productId !== '';
  }
  return false;
}
